import io from 'socket.io-client';

// Get socket URL from environment variables
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'https://legalplatform.co:4000';

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.listeners = new Map();
    this.connect(); // Connect on instantiation
  }

  // Initialize socket connection
  connect() {
    // Keep a SINGLE stable socket instance for the app's lifetime. Recreating
    // the socket (the old behavior) orphaned every .on() listener — e.g. the
    // call-accepted handler — so events arrived at a socket nobody was
    // listening on. If a socket already exists, just (re)connect it; socket.io
    // preserves listeners across its own reconnections.
    if (this.socket) {
      if (!this.socket.connected) this.socket.connect();
      return;
    }

    const socketOptions = {
      reconnection: true,
      reconnectionAttempts: 20,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      transports: ['websocket', 'polling'], // Allow polling fallback
      path: '/socket.io',
      autoConnect: true,
    };

    this.socket = io(SOCKET_URL, socketOptions);

    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket.id);
      this.connected = true;
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.connected = false;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this.connected = false;
      if (reason === 'io server disconnect') {
        this.socket.connect();
      }
    });
  }

  // Subscribe to an event
  subscribe(event, callback) {
    if (!this.socket || !this.socket.connected) {
      this.connect();
    }
    
    // Check if socket is actually initialized before calling on
    if (this.socket) {
        this.socket.on(event, callback);
    } else {
        // Retry after short delay if socket not ready
        setTimeout(() => this.subscribe(event, callback), 500);
        return () => {}; // Return dummy unsubscribe
    }

    // Return unsubscribe function
    return () => {
      if (this.socket) {
        this.socket.off(event, callback);
      }
    };
  }

  // Emit an event
  emit(event, data, callback) {
    if (!this.socket || !this.socket.connected) {
      this.connect();
    }
    
    if (this.socket) {
        return this.socket.emit(event, data, callback);
    }
  }
  
  // Get connection status (alias for backward compatibility)
  isConnected() {
    return this.checkConnection();
  }

  // Get connection status
  checkConnection() {
    return this.connected && this.socket && this.socket.connected;
  }
  
  // Get socket instance
  getSocket() {
    return this.socket;
  }
}

// Create a singleton instance
const socketServiceInstance = new SocketService();

export default socketServiceInstance;